import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { LogOut, User, ExternalLink, StickyNote, Users, Target, TrendingUp, Calendar } from 'lucide-react';
import Navigation from '../Navigation/Navigation';
import SalesBuddy from '../../apps/SalesBuddy/SalesBuddy';
import LanguageSwitcher from '../Navigation/LanguageSwitcher';
import TenantSelector from '../Navigation/TenantSelector';
import RoleSelectorModal from '../Navigation/RoleSelectorModal';
import CriticalAlerts from '../Notifications/CriticalAlerts';
import ThemeToggle from '../Navigation/ThemeToggle';
import OnboardingTour from '../Tutorial/OnboardingTour';
import HeaderHelpButton from '../Tutorial/HeaderHelpButton';
// import NavigationDebug from '../NavigationDebug';
// import RouteTest from '../RouteTest';
import { useTenantConfig } from '../../hooks/useTenantConfig';


const Layout = ({ children }) => {
  const { user, logout, refreshProfile } = useAuth();
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const { tenantConfig, loading: configLoading, error } = useTenantConfig();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  // Auto-refresh user profile if role information is missing (but only once)
  useEffect(() => {
    const refreshUserProfileIfNeeded = async () => {
      // Only refresh if user exists, has userId, but missing role information
      // And avoid refreshing if we already tried recently
      if (user && user.userId && !user.role && !user.roles && !user._profileRefreshAttempted) {
        console.log('🔍 LAYOUT_DEBUG: User role information missing, refreshing profile...');
        try {
          await refreshProfile();
        } catch (error) {
          console.error('🔍 LAYOUT_DEBUG: Failed to refresh profile:', error);
        }
      }
    };

    refreshUserProfileIfNeeded();
  }, [user?.userId, user?.role, user?.roles, refreshProfile]); // Only depend on specific properties

  const refreshProfileRef = useRef(refreshProfile);
  refreshProfileRef.current = refreshProfile;

  // Force profile refresh when navigating to settings (browser back/forward only)
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.pathname.includes('/settings') && user?.userId) {
        refreshProfileRef.current?.().catch(() => {});
      }
    };
    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [user?.userId]);


  const getUserRoles = () => {
    if (!user) return 'Loading...';
    if (user?.userType === 'admin' && user?.role) return user.role;
    if (user?.userType === 'regular') {
      const selectedRole = user.selectedRole?.RoleName || user.selectedRole;
      if (selectedRole) {
        return selectedRole
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
      }
      if (!user?.roles || user.roles.length === 0) return 'Loading roles...';
      return user.roles.map(role => role.RoleName || role).join(', ');
    }
    return 'User';
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Header with User Info and Logout - Mobile Optimized */}
      <header className="bg-gray-100 dark:bg-gray-800 shadow-sm border-b border-gray-300 dark:border-gray-700 transition-colors duration-300 sticky top-0 z-50">
        <div className="max-w-full w-full px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left Side - Logo and Company Name */}
            <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3 min-w-0 flex-1">
              {/* Company Logo */}
              {tenantConfig?.company_logo && (
                <img 
                  src={tenantConfig.company_logo} 
                  alt="Company Logo" 
                  className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              
              {/* Company Name with optional website link */}
              {tenantConfig?.website ? (
                <a 
                  href={tenantConfig.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-1 sm:space-x-2 hover:text-teal-600 transition-colors group min-w-0"
                >
                  <h1 className="text-[0.65rem] xs:text-xs sm:text-sm md:text-lg lg:text-xl font-semibold flex items-center space-x-1 sm:space-x-2 text-gray-900 dark:text-gray-100 group-hover:text-teal-600 dark:group-hover:text-teal-400">
                   <img src="/flowai.png" alt="Flow Agentic Sales Logo" className="h-8 w-8 sm:h-12 sm:w-12 md:h-16 md:w-16 lg:h-20 lg:w-20 object-contain flex-shrink-0" />
                   <span className="whitespace-nowrap">Flow Agentic Sales</span>
                  </h1>
                  <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 group-hover:text-teal-600 flex-shrink-0 hidden md:block" />
                </a>
              ) : (
                <h1 className="text-[0.65rem] xs:text-xs sm:text-sm md:text-lg lg:text-xl font-semibold flex items-center space-x-1 sm:space-x-2 text-gray-900 dark:text-gray-100">
                  <img src="/flowai.png" alt="Flow Agentic Sales Logo" className="h-8 w-8 sm:h-12 sm:w-12 md:h-14 md:w-14 lg:h-16 lg:w-16 object-contain flex-shrink-0" />
                  <span className="whitespace-nowrap">Flow Agentic Sales</span>
                </h1>
              )}
            </div>

            {/* Right Side - User Controls */}
            <div className="flex items-center flex-wrap justify-end gap-1 sm:gap-2 md:gap-3 lg:gap-4 flex-shrink-0">
              {/* Tenant Selector - Always visible, compact on mobile */}
              <div className="flex-shrink-0">
                <TenantSelector />
              </div>

              {/* Language Switcher - Hidden on very small screens, visible on sm+ */}
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              
              {/* User Information */}
              {user && (
                <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="h-7 w-7 sm:h-8 sm:w-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                    <Link 
                      to="/profile" 
                      className="hidden lg:block hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md p-1 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-teal-600 dark:hover:text-teal-400 cursor-pointer truncate max-w-[150px]">
                        {user.firstName} {user.lastName}
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Check if user has multiple roles
                          const allRoles = user?.roles || [];
                          const availableRoles = allRoles
                            .map(role => {
                              const roleName = role.RoleName || role;
                              return typeof roleName === 'string' ? roleName : String(roleName);
                            })
                            .filter((roleName, index, self) => self.indexOf(roleName) === index);
                          
                          if (availableRoles.length > 1) {
                            setIsRoleModalOpen(true);
                          }
                        }}
                        className="text-xs text-gray-500 dark:text-gray-400 truncate hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer text-left"
                        title={(() => {
                          const allRoles = user?.roles || [];
                          const availableRoles = allRoles
                            .map(role => {
                              const roleName = role.RoleName || role;
                              return typeof roleName === 'string' ? roleName : String(roleName);
                            })
                            .filter((roleName, index, self) => self.indexOf(roleName) === index);
                          return availableRoles.length > 1 ? 'Click to switch role' : '';
                        })()}
                      >
                        {getUserRoles()}
                      </button>
                    </Link>
                  </div>
                  
                  {/* Divider - Hidden on mobile */}
                  <div className="hidden sm:block h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                  
                  {/* Theme Toggle */}
                  <ThemeToggle className="mr-0 sm:mr-2" />
                  
                  {/* Divider - Hidden on mobile */}
                  <div className="hidden md:block h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                  
                  {/* Help Button - Hidden on small screens */}
                  <div className="hidden md:block">
                    <HeaderHelpButton />
                  </div>
                  
                  {/* Divider - Hidden on mobile */}
                  <div className="hidden sm:block h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={logout}
                    className="flex items-center space-x-1 sm:space-x-2 px-2 py-1 sm:px-3 sm:py-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200"
                    title={safeT('nav.logout', 'Logout')}
                  >
                    <LogOut className="h-4 w-4 flex-shrink-0" />
                    <span className="hidden md:inline text-xs sm:text-sm">{safeT('nav.logout', 'Logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Navigation */}
      <div className="sticky top-14 sm:top-16 z-40 bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 shadow-md transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-center">
            <Navigation />
          </div>
        </div>
      </div>
      
      {/* Mobile Quick Access Menu - Only visible on small screens */}
      <div className="lg:hidden bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 shadow-sm sticky top-[7.5rem] sm:top-32 z-30">
        <div className="max-w-7xl mx-auto px-2 py-3">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
          <Link 
            to="/notes" 
            className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[60px]"
          >
            <StickyNote className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{safeT('nav.salesNotes', 'Notes')}</span>
          </Link>
          
          <Link 
            to="/customers" 
            className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[60px]"
          >
            <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{safeT('nav.customers', 'Customers')}</span>
          </Link>
          
          <Link 
            to="/leads" 
            className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[60px]"
          >
            <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{safeT('nav.leads', 'Leads')}</span>
          </Link>
          
          <Link 
            to="/opportunities" 
            className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[60px]"
          >
            <TrendingUp className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{safeT('nav.opportunities', 'Opportunities')}</span>
          </Link>
          
          <Link 
            to="/activities" 
            className="flex flex-col items-center space-y-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-w-[60px]"
          >
            <Calendar className="h-5 w-5 text-red-600 dark:text-red-400" />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">{safeT('nav.activities', 'Activities')}</span>
          </Link>
          </div>
        </div>
      </div>
      
      {/* Critical Alerts - Display critical notifications */}
      <div className="max-w-full w-full px-2 sm:px-4 md:px-6 lg:px-8 pt-2 sm:pt-4">
        <CriticalAlerts />
      </div>
      
      {/* Main Content - Mobile Optimized Padding */}
      <div className="max-w-full w-full px-2 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6 pb-safe">
        {children}
      </div>
      
      {/* Debug components removed */}
      
      {/* Tutorial Components */}
      <OnboardingTour />
      
      <SalesBuddy />

      {/* Role Selector Modal */}
      <RoleSelectorModal 
        isOpen={isRoleModalOpen} 
        onClose={() => setIsRoleModalOpen(false)} 
      />
    </div>
  );
};

export default Layout; 